// Persisted TanStack Query cache for the web app.
//
// Why: every authenticated read costs a ~258 ms Worker→Supabase round-trip
// (measured: a Worker-only response is 7 ms, a Worker + single-query response
// is 265 ms). Without persistence, a page reload discards a perfectly good
// in-memory cache and pays that cost again for every query on the screen. With
// it, a returning user paints real content immediately and revalidates behind.
//
// SECURITY MODEL — read before changing anything here.
//
// localStorage is shared by every account that signs in on this browser. Three
// rules keep one user's data from reaching another:
//
//  1. Nothing is written while signed out, so an anonymous cache cannot exist.
//  2. Every write records the auth scope that produced it — user id, role id,
//     role_version, project_visibility, admin flag, auth schema version. A
//     restore whose recorded scope differs from the LIVE one is refused and the
//     stored blob erased. That covers a different user on this browser and the
//     same user after a role change.
//  3. purgePersistedCache() erases the blob on sign-out and on scope changes,
//     so the data stops existing rather than merely becoming unreadable.
//
// The scope is read at persist/restore time, never cached at module load. An
// earlier version derived it once during module evaluation, which was wrong in
// a way unit tests could not see: a user who signed in during the session had
// their data written under the boot-time "anonymous" scope, and the next reload
// then discarded it as foreign. Persistence silently never worked on the first
// session after login.
//
// Mutations are not persisted. Several responses carry short-lived signed
// storage URLs (`view_url` + `expires_in_seconds`); a rehydrated one is a dead
// link.

import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import type { Query } from '@tanstack/react-query';
import { buildAuthScopeKey } from '@task-tracker/shared';

/** Bump to invalidate every persisted cache — after a response-shape change, a
 *  query-key refactor, or anything else that makes old entries wrong. */
// v2 (2026-08-11): the notification list key gained a 'list' segment
// (['notifications', filters] → ['notifications', 'list', filters]) and the
// settings response gained `typeGroups`. A hydrated v1 entry would be read as
// an empty settings catalogue.
const CACHE_VERSION = 'v2';

const CACHE_KEY = 'cranesafe-query-cache';

/** Cached data older than this is discarded on restore rather than shown. */
export const PERSIST_MAX_AGE_MS = 1000 * 60 * 60 * 24; // 24 hours

const ANONYMOUS = 'anonymous';

/**
 * Query-key prefixes that must never be written to disk.
 *
 * Keep this tight — the default is to persist, because persistence is the
 * point. Add a prefix only when the payload is short-lived, credential-bearing,
 * or meaningless after a reload.
 */
const NEVER_PERSIST_PREFIXES: readonly string[] = [
  // Transient validation for a handle the user is currently typing.
  'username-available',
  // A whole project chat transcript, including signed attachment URLs. Two
  // reasons: localStorage is a ~5 MB budget shared by every other query, and a
  // busy project's history would crowd out the rest of the cache (or blow the
  // quota outright); and the attachment `signed_url`s expire in 24 h, so a
  // rehydrated transcript serves dead links. The delta poll refills it in one
  // round trip anyway.
  'project-messages',
];

export function isPersistableQuery(query: Query): boolean {
  // Only successful reads are worth keeping. Persisting an error would
  // rehydrate a failure — including a 403 from a since-corrected permission —
  // and present it as current.
  if (query.state.status !== 'success') return false;

  const root = query.queryKey[0];
  if (typeof root !== 'string') return false;
  return !NEVER_PERSIST_PREFIXES.includes(root);
}

/**
 * The auth scope of the session Supabase currently has in localStorage.
 *
 * Supabase's web client stores it under `sb-<project-ref>-auth-token` —
 * verified against @supabase/supabase-js: the key is built as
 * `sb-${hostname.split('.')[0]}-auth-token`, and auth-js writes the session as
 * plain `JSON.stringify(session)` with `user` included.
 *
 * That last part only holds while no `userStorage` is configured on the client.
 * If one is ever added, auth-js strips `user` from the main blob and this
 * returns ANONYMOUS — persistence quietly switches off rather than
 * mis-attributing a cache. Every failure path returns ANONYMOUS for the same
 * reason: it blocks writes and fails restores closed.
 */
export function readCurrentAuthScope(): string {
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !/^sb-.*-auth-token$/.test(key)) continue;

      const raw = window.localStorage.getItem(key);
      if (!raw) continue;

      const parsed = JSON.parse(raw) as {
        access_token?: string;
        user?: { id?: string };
      } | null;

      const token = parsed?.access_token ?? null;
      const userId = parsed?.user?.id ?? null;
      if (!token || !userId) continue;

      return buildAuthScopeKey({ token, userId });
    }
  } catch {
    // Corrupt or inaccessible storage (private mode, quota, hostile JSON).
  }
  return ANONYMOUS;
}

const basePersister = createSyncStoragePersister({
  key: CACHE_KEY,
  storage: window.localStorage,
  // Batch rapid cache churn into one write; serializing the whole cache on
  // every query settle would be a main-thread cost on list-heavy screens.
  throttleTime: 1_000,
});

function eraseAll(): void {
  try {
    window.localStorage.removeItem(CACHE_KEY);
  } catch {
    // Storage being unavailable also means nothing was written.
  }
}

/** The cache identity for a given auth scope: version AND who it belongs to. */
function scopedBuster(scope: string): string {
  return `${CACHE_VERSION}:${scope}`;
}

/**
 * localStorage persister wrapped with auth-scope enforcement.
 *
 * The scope is stamped INSIDE the persisted payload's `buster` rather than kept
 * in a sibling key. That matters: `basePersister.persistClient` throttles its
 * write by a second, so a separate scope key would be written eagerly while the
 * blob it describes lands later — two records that can disagree for a full
 * second, and across a purge. Folding the scope into the payload makes identity
 * and data a single atomic write that cannot desync by construction.
 *
 * The library performs its own `buster` equality check after restoreClient
 * returns, against `persistOptions.buster` (the bare CACHE_VERSION). A restored
 * client therefore has its buster normalised back before being handed over.
 */
export const queryPersister: Persister = {
  persistClient: async (client: PersistedClient) => {
    const scope = readCurrentAuthScope();
    if (scope === ANONYMOUS) return; // Rule 1.

    await basePersister.persistClient({ ...client, buster: scopedBuster(scope) });
  },

  restoreClient: async () => {
    const stored = await basePersister.restoreClient();
    if (!stored) return undefined;

    const liveScope = readCurrentAuthScope();

    // Rule 2 — covers a different user on this browser, the same user after a
    // role change, a stale CACHE_VERSION, and the signed-out case.
    if (liveScope === ANONYMOUS || stored.buster !== scopedBuster(liveScope)) {
      eraseAll();
      return undefined;
    }

    return { ...stored, buster: CACHE_VERSION };
  },

  removeClient: async () => eraseAll(),
};

export const persistOptions = {
  persister: queryPersister,
  maxAge: PERSIST_MAX_AGE_MS,
  buster: CACHE_VERSION,
  dehydrateOptions: {
    shouldDehydrateQuery: isPersistableQuery,
    shouldDehydrateMutation: () => false,
  },
} as const;

/**
 * Erase the persisted cache. Called on sign-out and on any auth-scope change.
 *
 * The scope check already refuses to hydrate a foreign cache; this makes the
 * data absent rather than merely unreadable.
 */
export function purgePersistedCache(): void {
  eraseAll();
}

export const __testing = {
  CACHE_VERSION,
  CACHE_KEY,
  ANONYMOUS,
  NEVER_PERSIST_PREFIXES,
  scopedBuster,
};
